import { ErrorType } from 'src/app/core/routing/error-types';
import { finalize, tap } from 'rxjs/operators';
import { SpinnerService } from './../../../base/spinner/spinner.service';
import { ToastService } from './../../../base/services/toast.service';
import { EmailAccountSettingsClient, FilterOperator, EmailAccountSettingsDTO, CreateEmailAccountSettingsDTO, UpdateEmailAccountSettingsDTO, DeleteEmailAccountSettingsResultDTO, EmailProcessResultDTO } from './../../../base/models/models.swagger.generated';
import { IAdaptorService } from './../../../base/shared/syncfusion/adaptor-service.interface';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { FilterDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { FormGroup } from '@angular/forms';
import { EmailSettingsFormType } from './email-settings-form-type';

@Injectable({
  providedIn: 'root',
})
export class EmailSettingsService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private emailAccountSettingsClient: EmailAccountSettingsClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
    ) {
      super();
    }

  // use this method to get in email accounts with limited information
  getInEmailAccounts(): Observable<EmailAccountSettingsDTO[]> {
    return this.emailAccountSettingsClient.getInEmailAccounts();
  }

  // use this method to get in email accounts with limited information
  getOutEmailAccounts(): Observable<EmailAccountSettingsDTO[]> {
    return this.emailAccountSettingsClient.getOutEmailAccounts();
  }

  getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!this.showInactive) {
      if (!filters) {
        filters = [];
      }
      filters.push(new FilterDTO({isComplex: false, field: 'Enabled', operator: FilterOperator.Equal, value: 'true'}));
    }
    return this.emailAccountSettingsClient.searchEmailAccountSettings(skip, take, sortBy, sortDirection, filters).pipe(
      tap((result: any) => {
        if (result.items) {
          result.items.forEach((item) => {
            if (!item.client) {
              item.client = {} as any;
            }
          })
        }
      })
    );
  }

  addRecord(data: CreateEmailAccountSettingsDTO): Observable<EmailAccountSettingsDTO> {
    this.spinnerService.show();
    return this.emailAccountSettingsClient.createEmailAccountSettings(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.settings.email-settings.messages.add-title',
          'i18n.domain.settings.email-settings.messages.add-success-message');
      }),
      finalize(() => this.spinnerService.hide()),
      catchError(err => {
        this.toastService.showError(
          'i18n.domain.settings.email-settings.messages.add-title',
          'i18n.domain.settings.email-settings.messages.add-failed-message');
        return throwError(() => err);
      })
    );
  }

  updateRecord(data: UpdateEmailAccountSettingsDTO): Observable<EmailAccountSettingsDTO> {
    this.spinnerService.show();
    return this.emailAccountSettingsClient.updateEmailAccountSettings(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.settings.email-settings.messages.edit-title',
          'i18n.domain.settings.email-settings.messages.edit-success-message');
      }),
      finalize(() => this.spinnerService.hide()),
      catchError(err => {
        this.toastService.showError(
          'i18n.domain.settings.email-settings.messages.edit-title',
          'i18n.domain.settings.email-settings.messages.edit-failed-message');
        return throwError(() => err);
      })
    );
  }

  deleteRecord(id: number): Observable<DeleteEmailAccountSettingsResultDTO> {
    return this.emailAccountSettingsClient.deleteEmailAccountSettings(id)
    .pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.settings.email-settings.messages.delete-title',
          'i18n.domain.settings.email-settings.messages.delete-success-message');
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public sendTestEmail(emailSettingsData: FormGroup<EmailSettingsFormType>, emailAddress: string): Observable<EmailProcessResultDTO> {
    if (!emailSettingsData) {
      return null;
    }
    const formValues = emailSettingsData.value;
    const emailSettings = this.getCreateEmailAccountSettingsDTOFromForm(emailSettingsData);
    this.spinnerService.show();
    return this.emailAccountSettingsClient.sendTestEmail(emailAddress, emailSettings).pipe(
      finalize(() => this.spinnerService.hide()),
      catchError((error: any) => {
        this.spinnerService.hide();
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        return throwError(() => error);
      }),
    );
  }

  public getCreateEmailAccountSettingsDTOFromForm(emailSettingsData: FormGroup<EmailSettingsFormType>): CreateEmailAccountSettingsDTO {
    if (!emailSettingsData) {
      return null;
    }
    const formValues = emailSettingsData.value;
    return new CreateEmailAccountSettingsDTO({
      accountName:  formValues.accountName,
      accountType:  formValues.accountType,
      direction: formValues.direction,
      receiveOnlyMailsWithAttachments: formValues.receiveOnlyMailsWithAttachments,
      emailAddress: formValues.emailAddress,
      password:  formValues.password,
      serverUrl: formValues.serverUrl,
      userName: formValues.userName,
      useSSL: formValues.useSSL,
      port: formValues.port,
      azureClientId: formValues.azureClientId,
      azureClientSecret: formValues.azureClientSecret,
      azureTenantId: formValues.azureTenantId
    });
  }
}
